import { useCallback, useEffect, useRef, useState } from 'react';
import { useCouponsStatistics } from '../../../hooks/useStatistics';
import { useReleaseReasons } from '../../../hooks/useReleaseReasons';
import { useRecLegalEntities } from '../../../hooks/useLegalEntities';
import {
  GERENCIALES_REPORT_OPTIONS,
  GERENCIALES_REPORT_TYPE,
  GERENCIALES_DATE_RANGE_REPORTS,
  GERENCIALES_MESSAGES,
  GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE,
  GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE,
} from '../constants/gerencialesConstants';

/**
 * Obtiene el rango del mes actual en formato YYYY-MM-DD.
 * @returns {{dateStart: string, dateEnd: string}} Rango del mes actual.
 */
const getMonthDateRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);

  const toDateString = (dateValue) => dateValue.toISOString().split('T')[0];

  return {
    dateStart: toDateString(start),
    dateEnd: toDateString(end),
  };
};

const getDefaultPaymentsFilters = () => ({
  ...getMonthDateRange(),
  month: '',
  year: '',
  legalEntity: '',
});

/**
 * Hook para obtener y mantener estadisticas gerenciales de contratos.
 * @returns {Object} Estado y acciones para la vista Gerenciales.
 */
export const useGerencialesStatistics = () => {
  const { mutateAsync: fetchStatistics } = useCouponsStatistics();
  const { data: releaseReasons = [], isLoading: releaseReasonsLoading } = useReleaseReasons();
  const { data: recLegalEntities = [], isLoading: recLegalEntitiesLoading } =
    useRecLegalEntities();
  const [reportType, setReportType] = useState(GERENCIALES_REPORT_TYPE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [header, setHeader] = useState(null);
  const [filters, setFilters] = useState(() => getDefaultPaymentsFilters());
  const [appSubscribersPagination, setAppSubscribersPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    total: 0,
    pages: 0,
  });
  const [archivedContractsPagination, setArchivedContractsPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    total: 0,
    pages: 0,
  });
  const [appSubscribersFilters, setAppSubscribersFilters] = useState({
    appUsage: 'all',
  });
  const [archivedContractsFilters, setArchivedContractsFilters] = useState({
    contractNumber: '',
    releaseReason: '',
  });
  const loadStatisticsRef = useRef(null);
  const lastReportTypeRef = useRef(null);

  const requiresDateRange = GERENCIALES_DATE_RANGE_REPORTS.includes(reportType);
  const usesMonthOrYear =
    (filters.month !== '' && filters.month !== null) ||
    (filters.year !== '' && filters.year !== null);

  /**
   * Maneja el cambio de filtros de fecha.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del input.
   * @returns {void} Actualiza el filtro.
   */
  const handleDateChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      month: '',
      year: '',
    }));
    if (error) {
      setError(null);
    }
  };

  /**
   * Maneja el cambio de filtros adicionales del reporte de pagos.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del select.
   * @returns {void} Actualiza filtros de pagos.
   */
  const handlePaymentsFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'month' || name === 'year'
        ? {
            dateStart: '',
            dateEnd: '',
          }
        : null),
    }));
    if (error) {
      setError(null);
    }
  };

  /**
   * Solicita al backend las estadisticas gerenciales.
   * @returns {Promise<void>} Promesa de carga.
   */
  const loadStatistics = useCallback(async (overrides = {}) => {
    const paginationState =
      reportType === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE
        ? appSubscribersPagination
        : archivedContractsPagination;
    const pageOverride = overrides.page ?? paginationState.page;
    const rowsPerPageOverride = overrides.rowsPerPage ?? paginationState.rowsPerPage;

    setLoading(true);
    setError(null);

    try {
      if (requiresDateRange && !usesMonthOrYear) {
        if (!filters.dateStart || !filters.dateEnd) {
          setError(GERENCIALES_MESSAGES.ERROR.DATES_REQUIRED);
          setResults(null);
          setHeader(null);
          return;
        }
        if (filters.dateStart > filters.dateEnd) {
          setError(GERENCIALES_MESSAGES.ERROR.INVALID_RANGE);
          setResults(null);
          setHeader(null);
          return;
        }
      }

      if (reportType === GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE) {
        const { dateStart, dateEnd } = filters;
        if ((dateStart && !dateEnd) || (dateEnd && !dateStart)) {
          setError(GERENCIALES_MESSAGES.ERROR.DATES_REQUIRED);
          setResults(null);
          setHeader(null);
          return;
        }
        if (dateStart && dateEnd && dateStart > dateEnd) {
          setError(GERENCIALES_MESSAGES.ERROR.INVALID_RANGE);
          setResults(null);
          setHeader(null);
          return;
        }
      }

      const payload = { reportType };
      if (requiresDateRange) {
        if (usesMonthOrYear) {
          if (filters.month !== '') {
            payload.month = Number(filters.month);
          }
          if (filters.year !== '') {
            payload.year = Number(filters.year);
          }
        } else {
          payload.dateStart = filters.dateStart;
          payload.dateEnd = filters.dateEnd;
        }
        if (filters.legalEntity) {
          payload.legalEntity = filters.legalEntity;
        }
      }
      if (reportType === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE) {
        if (appSubscribersFilters.appUsage !== 'all') {
          payload.appUsage = appSubscribersFilters.appUsage;
        }
        payload.page = pageOverride;
        payload.perPage = rowsPerPageOverride;
      }
      if (reportType === GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE) {
        if (archivedContractsFilters.contractNumber) {
          payload.contractNumber = archivedContractsFilters.contractNumber.trim();
        }
        if (archivedContractsFilters.releaseReason) {
          payload.releaseReason = archivedContractsFilters.releaseReason;
        }
        if (filters.dateStart && filters.dateEnd) {
          payload.dateStart = filters.dateStart;
          payload.dateEnd = filters.dateEnd;
        }
        payload.page = pageOverride;
        payload.perPage = rowsPerPageOverride;
      }

      const response = await fetchStatistics(payload);
      setResults(response?.results || null);
      setHeader(response?.header || null);
      if (reportType === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE) {
        const pagination = response?.results?.pagination || {};
        setAppSubscribersPagination((prev) => ({
          ...prev,
          total: pagination.total ?? 0,
          pages: pagination.pages ?? 0,
          page: pagination.page ?? pageOverride,
          rowsPerPage: pagination.per_page ?? rowsPerPageOverride,
        }));
      }
      if (reportType === GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE) {
        const pagination = response?.results?.pagination || {};
        setArchivedContractsPagination((prev) => ({
          ...prev,
          total: pagination.total ?? 0,
          pages: pagination.pages ?? 0,
          page: pagination.page ?? pageOverride,
          rowsPerPage: pagination.per_page ?? rowsPerPageOverride,
        }));
      }
    } catch (fetchError) {
      console.error('Error fetching managerial statistics:', fetchError);
      setError('Error al obtener las estadisticas.');
      setResults(null);
      setHeader(null);
    } finally {
      setLoading(false);
    }
  }, [
    fetchStatistics,
    reportType,
    requiresDateRange,
    filters,
    appSubscribersFilters.appUsage,
    archivedContractsFilters.contractNumber,
    archivedContractsFilters.releaseReason,
    appSubscribersPagination,
    archivedContractsPagination,
    usesMonthOrYear,
  ]);

  useEffect(() => {
    loadStatisticsRef.current = loadStatistics;
  }, [loadStatistics]);

  useEffect(() => {
    if (lastReportTypeRef.current === reportType) return;
    lastReportTypeRef.current = reportType;
    if (loadStatisticsRef.current) {
      loadStatisticsRef.current();
    }
  }, [reportType]);

  /**
   * Maneja el cambio de tipo de reporte.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del select.
   * @returns {void} Actualiza el tipo de reporte.
   */
  const handleReportTypeChange = (event) => {
    const { value } = event.target;
    const isValid = GERENCIALES_REPORT_OPTIONS.some((option) => option.value === value);
    if (!isValid) return;
    setReportType(value);
    setResults(null);
    setHeader(null);
    if (GERENCIALES_DATE_RANGE_REPORTS.includes(value)) {
      setFilters(getDefaultPaymentsFilters());
    } else {
      setFilters({
        dateStart: '',
        dateEnd: '',
        month: '',
        year: '',
        legalEntity: '',
      });
    }
    if (value === GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE) {
      setAppSubscribersFilters({
        appUsage: 'all',
      });
      setAppSubscribersPagination({
        page: 1,
        rowsPerPage: 10,
        total: 0,
        pages: 0,
      });
    }
    if (value === GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE) {
      setArchivedContractsFilters({
        contractNumber: '',
        releaseReason: '',
      });
      setArchivedContractsPagination({
        page: 1,
        rowsPerPage: 10,
        total: 0,
        pages: 0,
      });
    }
    if (error) {
      setError(null);
    }
  };

  /**
   * Maneja el cambio de filtros del reporte de app.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del select.
   * @returns {void} Actualiza filtros de app.
   */
  const handleAppSubscribersFilterChange = (event) => {
    const { name, value } = event.target;
    setAppSubscribersFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setAppSubscribersPagination((prev) => ({
      ...prev,
      page: 1,
    }));
    if (error) {
      setError(null);
    }
  };

  /**
   * Maneja el cambio de filtros del reporte de bajas.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del input/select.
   * @returns {void} Actualiza filtros de bajas.
   */
  const handleArchivedContractsFilterChange = (event) => {
    const { name, value } = event.target;
    setArchivedContractsFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setArchivedContractsPagination((prev) => ({
      ...prev,
      page: 1,
    }));
    if (error) {
      setError(null);
    }
  };

  /**
   * Maneja el cambio de página del reporte de app.
   * @param {import('react').ChangeEvent<unknown>} _event - Evento de paginación.
   * @param {number} value - Página seleccionada.
   * @returns {void} Actualiza la página.
   */
  const handleAppSubscribersPageChange = (_event, value) => {
    setAppSubscribersPagination((prev) => ({
      ...prev,
      page: value,
    }));
    loadStatistics({ page: value });
  };

  /**
   * Maneja el cambio de cantidad de filas del reporte de app.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del select.
   * @returns {void} Actualiza la cantidad de filas.
   */
  const handleAppSubscribersRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setAppSubscribersPagination((prev) => ({
        ...prev,
        page: 1,
        rowsPerPage: value,
      }));
      loadStatistics({ page: 1, rowsPerPage: value });
    }
  };

  /**
   * Maneja el cambio de página del reporte de bajas.
   * @param {import('react').ChangeEvent<unknown>} _event - Evento de paginación.
   * @param {number} value - Página seleccionada.
   * @returns {void} Actualiza la página.
   */
  const handleArchivedContractsPageChange = (_event, value) => {
    setArchivedContractsPagination((prev) => ({
      ...prev,
      page: value,
    }));
    loadStatistics({ page: value });
  };

  /**
   * Maneja el cambio de cantidad de filas del reporte de bajas.
   * @param {import('react').ChangeEvent<HTMLInputElement>} event - Evento del select.
   * @returns {void} Actualiza la cantidad de filas.
   */
  const handleArchivedContractsRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setArchivedContractsPagination((prev) => ({
        ...prev,
        page: 1,
        rowsPerPage: value,
      }));
      loadStatistics({ page: 1, rowsPerPage: value });
    }
  };

  /**
   * Obtiene data completa para exportar el reporte de app.
   * @returns {Promise<Array<Object>>} Filas completas del reporte.
   */
  const fetchAppSubscribersExport = async () => {
    const total = appSubscribersPagination.total || 0;
    const perPage = total > 0 ? total : 1000;

    const payload = {
      reportType: GERENCIALES_APP_SUBSCRIBERS_REPORT_TYPE,
      ...(appSubscribersFilters.appUsage !== 'all'
        ? { appUsage: appSubscribersFilters.appUsage }
        : {}),
      page: 1,
      perPage,
    };

    const response = await fetchStatistics(payload);
    return response?.results?.table || [];
  };

  /**
   * Obtiene data completa para exportar el reporte de bajas.
   * @returns {Promise<Array<Object>>} Filas completas del reporte.
   */
  const fetchArchivedContractsExport = async () => {
    const total = archivedContractsPagination.total || 0;
    const perPage = total > 0 ? total : 1000;

    const payload = {
      reportType: GERENCIALES_ARCHIVED_CONTRACTS_REPORT_TYPE,
      ...(archivedContractsFilters.contractNumber
        ? { contractNumber: archivedContractsFilters.contractNumber.trim() }
        : {}),
      ...(archivedContractsFilters.releaseReason
        ? { releaseReason: archivedContractsFilters.releaseReason }
        : {}),
      ...(filters.dateStart && filters.dateEnd
        ? {
            dateStart: filters.dateStart,
            dateEnd: filters.dateEnd,
          }
        : {}),
      page: 1,
      perPage,
    };

    const response = await fetchStatistics(payload);
    return response?.results?.table || [];
  };

  return {
    reportType,
    loading,
    error,
    results,
    header,
    filters,
    appSubscribersFilters,
    appSubscribersPagination,
    archivedContractsFilters,
    archivedContractsPagination,
    releaseReasons,
    releaseReasonsLoading,
    recLegalEntities,
    recLegalEntitiesLoading,
    reload: loadStatistics,
    handleReportTypeChange,
    handleDateChange,
    handlePaymentsFilterChange,
    handleAppSubscribersFilterChange,
    handleAppSubscribersPageChange,
    handleAppSubscribersRowsPerPageChange,
    handleArchivedContractsFilterChange,
    handleArchivedContractsPageChange,
    handleArchivedContractsRowsPerPageChange,
    fetchAppSubscribersExport,
    fetchArchivedContractsExport,
  };
};
